import React, { useState } from 'react';
import { LayoutDashboard, Users, ScanLine, Wallet } from 'lucide-react';
import Reveal from './Reveal';

const Frame = ({ title, children }) => (
  <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#0A192F]/80 shadow-2xl shadow-black/40 backdrop-blur-md">
    <div className="flex items-center gap-2 border-b border-white/5 bg-white/[0.03] px-4 py-2.5">
      <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
      <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
      <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
      <span className="ms-auto text-[11px] font-semibold text-slate-400">{title}</span>
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </div>
);

const Tile = ({ label, value, tone = 'teal' }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
    <div className={`font-display text-xl font-black tabular-nums ${tone === 'orange' ? 'text-orange-400' : tone === 'white' ? 'text-white' : 'text-teal-300'}`}>{value}</div>
    <div className="mt-0.5 text-[11px] text-slate-400">{label}</div>
  </div>
);

const Chip = ({ text, tone }) => (
  <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${tone === 'ok' ? 'bg-emerald-500/15 text-emerald-300' : tone === 'late' ? 'bg-red-500/15 text-red-300' : 'bg-amber-500/15 text-amber-300'}`}>{text}</span>
);

const Row = ({ name, meta, children }) => (
  <div className="flex items-center gap-3 border-b border-white/5 py-2.5 last:border-0">
    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-teal-500/10 text-[11px] font-bold text-teal-300">{name.charAt(0)}</span>
    <div className="min-w-0 flex-1">
      <div className="truncate text-xs font-bold text-white">{name}</div>
      <div className="truncate text-[11px] text-slate-500">{meta}</div>
    </div>
    {children}
  </div>
);

const Bars = ({ values, labels }) => (
  <div className="flex h-28 items-stretch gap-2">
    {values.map((v, i) => (
      <div key={i} className="flex h-full flex-1 flex-col items-center justify-end gap-1.5">
        <div className="w-full rounded-t-md bg-gradient-to-t from-teal-600/40 to-teal-400" style={{ height: `${v}%` }} />
        <span className="text-[9px] text-slate-500">{labels[i]}</span>
      </div>
    ))}
  </div>
);

const PANELS = {
  dashboard: {
    label: 'لوحة التحكم',
    icon: LayoutDashboard,
    title: 'لوحة تحكم المنشأة',
    note: 'كل مؤشرات منشأتك في شاشة واحدة: اللاعبون النشطون، الاشتراكات القريبة من الانتهاء، الحضور الأسبوعي، وآخر العمليات.',
    render: () => (
      <>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Tile label="لاعب نشط" value="312" />
          <Tile label="اشتراك ينتهي خلال 7 أيام" value="18" tone="orange" />
          <Tile label="نسبة الحضور" value="94%" tone="white" />
          <Tile label="إيراد الشهر" value="48,200" />
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-5">
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 sm:col-span-3">
            <div className="mb-2 text-[11px] font-bold text-slate-300">الحضور الأسبوعي</div>
            <Bars values={[62, 88, 54, 76, 45, 70, 38]} labels={['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت']} />
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 sm:col-span-2">
            <div className="mb-1 text-[11px] font-bold text-slate-300">آخر الأنشطة</div>
            {['تسجيل حضور — فئة تحت 14', 'دفعة اشتراك شهري', 'إضافة لاعب للفريق الأول'].map((t) => (
              <div key={t} className="border-b border-white/5 py-2 text-[11px] text-slate-400 last:border-0">{t}</div>
            ))}
          </div>
        </div>
      </>
    ),
  },
  players: {
    label: 'اللاعبون والاشتراكات',
    icon: Users,
    title: 'ملفات اللاعبين والاشتراكات',
    note: 'كل لاعب بملفه: الفئة، وليّ الأمر، حالة الاشتراك، المدفوعات والمتأخرات — مع تنبيه تلقائي قبل الانتهاء.',
    render: () => (
      <>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="rounded-lg bg-white/[0.04] px-3 py-1.5 text-[11px] text-slate-300">بحث: اسم / جوال / رقم عضوية</span>
          <Chip text="نشط 268" tone="ok" />
          <Chip text="قارب على الانتهاء 26" tone="warn" />
          <Chip text="متأخر 18" tone="late" />
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3">
          <Row name="راكان العتيبي" meta="تحت 14 — الفريق الأول"><Chip text="نشط · 42 يوم" tone="ok" /></Row>
          <Row name="زكريا المالكي" meta="تحت 16 — فريق البراعم"><Chip text="متأخر 68 يوم" tone="late" /></Row>
          <Row name="عبدالله القحطاني" meta="تحت 12 — أكاديمية النخبة"><Chip text="ينتهي خلال 5 أيام" tone="warn" /></Row>
          <Row name="فيصل الشهري" meta="تحت 18 — الفريق الأول"><Chip text="نشط · 180 يوم" tone="ok" /></Row>
        </div>
      </>
    ),
  },
  attendance: {
    label: 'الحضور بالباركود',
    icon: ScanLine,
    title: 'حضور وانصراف في ثوانٍ',
    note: 'بطاقة لكل لاعب وموظف برمز QR، والمسح يسجّل الحضور فوراً ويربطه بالحصة والفئة والمدرب.',
    render: () => (
      <div className="grid gap-4 sm:grid-cols-5">
        <div className="flex flex-col items-center justify-center rounded-xl border border-teal-500/30 bg-teal-500/[0.06] p-4 sm:col-span-2">
          <div className="grid h-24 w-24 grid-cols-5 gap-0.5 rounded-lg bg-white p-1.5">
            {Array.from({ length: 25 }).map((_, i) => (
              <span key={i} className={`rounded-[1px] ${[0, 1, 2, 5, 7, 10, 12, 13, 16, 18, 20, 21, 24, 4, 9, 14].includes(i) ? 'bg-[#0A192F]' : 'bg-transparent'}`} />
            ))}
          </div>
          <div className="mt-3 text-xs font-bold text-white">بطاقة اللاعب</div>
          <div className="text-[11px] text-teal-300">تم التحقق · 07:58 ص</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 sm:col-span-3">
          <Row name="ماجد الحربي" meta="حضور · تحت 16"><span className="text-[11px] tabular-nums text-emerald-300">07:58</span></Row>
          <Row name="سعود الدوسري" meta="حضور · تحت 14"><span className="text-[11px] tabular-nums text-emerald-300">08:02</span></Row>
          <Row name="أ. خالد المطيري" meta="مدرب · تسجيل دخول"><span className="text-[11px] tabular-nums text-emerald-300">08:05</span></Row>
          <Row name="نواف العنزي" meta="تأخر عن الحصة"><span className="text-[11px] tabular-nums text-amber-300">08:41</span></Row>
        </div>
      </div>
    ),
  },
  finance: {
    label: 'المالية والتقارير',
    icon: Wallet,
    title: 'مالية منشأتك وتقاريرها',
    note: 'إيرادات ومصروفات وفواتير معزولة لكل منشأة، وتقارير جاهزة للطباعة أو التصدير إلى Excel وPDF.',
    render: () => (
      <>
        <div className="grid grid-cols-3 gap-3">
          <Tile label="إيرادات الشهر" value="48,200" />
          <Tile label="مصروفات الشهر" value="19,450" tone="orange" />
          <Tile label="الصافي" value="28,750" tone="white" />
        </div>
        <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-3">
          <div className="mb-2 flex items-center justify-between text-[11px] font-bold text-slate-300">
            <span>الإيرادات مقابل المصروفات</span>
            <span className="text-slate-500">آخر 6 أشهر</span>
          </div>
          <Bars values={[48, 62, 55, 78, 70, 92]} labels={['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو']} />
        </div>
      </>
    ),
  },
};

export const LandingShowcase = ({ landing }) => {
  const [tab, setTab] = useState('dashboard');
  const active = PANELS[tab];

  return (
    <section id="showcase" className="border-y border-teal-900/20 bg-[#0A192F]/30 py-20 md:py-28" data-testid="landing-showcase">
      <div className="mx-auto max-w-7xl px-5">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-bold uppercase tracking-wider text-teal-400">من داخل المنصة</span>
            <h2 className="font-display mt-3 text-2xl font-bold text-white sm:text-3xl lg:text-4xl" data-testid="landing-showcase-title">
              {landing?.showcase_title || 'شاهد تسيار من الداخل'}
            </h2>
            <p className="mt-3 text-slate-400" data-testid="landing-showcase-subtitle">
              {landing?.showcase_subtitle || 'جولة سريعة في الشاشات التي ستستخدمها يومياً'}
            </p>
          </div>
        </Reveal>

        <div className="mt-8 flex flex-wrap justify-center gap-2" data-testid="landing-showcase-tabs">
          {Object.entries(PANELS).map(([key, p]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`landing-showcase-tab-${key}`}
              className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold transition-colors sm:text-sm ${
                tab === key ? 'border-teal-500/60 bg-teal-600/20 text-white' : 'border-white/10 text-slate-400 hover:border-white/30 hover:text-white'
              }`}
            >
              <p.icon className="h-4 w-4" /> {p.label}
            </button>
          ))}
        </div>

        <div className="mt-7 grid items-center gap-8 lg:grid-cols-5">
          <div className="order-2 lg:order-1 lg:col-span-2">
            <h3 className="font-display text-lg font-bold text-white md:text-xl" data-testid="landing-showcase-panel-title">{active.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">{active.note}</p>
          </div>
          <div className="order-1 lg:order-2 lg:col-span-3" data-testid="landing-showcase-panel">
            <Frame title={active.label}>{active.render()}</Frame>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LandingShowcase;
